import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, LogOut, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { CATEGORIES } from "./config";
import { imgSrc } from "./store";
import { Footer, Header } from "./components/layout";
import { Chapter } from "./motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", category: "sarees", subcategory: "", price: "", mrp: "", description: "", sizes: [], colors: [], images: [], in_stock: true, featured: false, new_arrival: false };
const ALPHA_SIZES = ["Free Size", "S", "M", "L", "XL", "2XL", "3XL", "4XL"];
const TAPE_SIZES = ["28", "30", "32", "34", "36", "38", "40", "42", "44", "46", "48", "50"];
export const COLOR_OPTIONS = [
  ["Maroon", "#800020"], ["Red", "#C0392B"], ["Pink", "#E75480"], ["Orange", "#E67E22"],
  ["Yellow", "#F1C40F"], ["Gold", "#C9A227"], ["Green", "#1E8449"], ["Teal", "#148F77"],
  ["Blue", "#2E5EAA"], ["Navy", "#1B2A4A"], ["Purple", "#7D3C98"], ["Black", "#1C1C1C"],
  ["White", "#FFFFFF"], ["Cream", "#F5EAD0"], ["Grey", "#7F8C8D"], ["Multi", "multi"],
];
export const COLOR_HEX = Object.fromEntries(COLOR_OPTIONS);

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("hsm_admin") || ""}` });

function AdminLogin({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem("hsm_admin", data.token);
      onLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <main className="inner-page center-page">
      <Chapter index="—" eyebrow="OWNER ACCESS" title="Admin login" center />
      <form data-testid="admin-login-form" className="enquiry-form admin-login" onSubmit={submit}>
        <label>Email<input data-testid="admin-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="username" /></label>
        <label>Password<input data-testid="admin-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="current-password" /></label>
        <button data-testid="admin-login-button" className="button primary" disabled={loading}>{loading ? "Checking…" : "Login"} <ArrowRight size={16} /></button>
      </form>
    </main>
  );
}

function ProductForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await axios.post(`${API}/admin/upload`, data, { headers: authHeader() });
      setForm((f) => ({ ...f, images: [...f.images, res.path] }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <form data-testid="product-form" className="enquiry-form product-form" onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <div className="checkout-grid">
        <label>Product name<input data-testid="product-name-input" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Banarasi Silk Saree" /></label>
        <label>Category
          <select data-testid="product-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            <option value="wedding">Wedding Collection</option>
          </select>
        </label>
      </div>
      <div className="checkout-grid">
        <label>MRP (₹) — optional<input data-testid="product-mrp-input" type="number" min="0" step="1" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} placeholder="Original price" /></label>
        <label>Selling price (₹) — optional<input data-testid="product-price-input" type="number" min="0" step="1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Leave blank for price on enquiry" /></label>
      </div>
      {form.mrp && form.price && Number(form.mrp) > Number(form.price) && (
        <p className="discount-preview" data-testid="discount-preview">
          Customers will see: <s>₹{Number(form.mrp).toLocaleString("en-IN")}</s> <b>₹{Number(form.price).toLocaleString("en-IN")}</b> — <span>{Math.round(((Number(form.mrp) - Number(form.price)) / Number(form.mrp)) * 100)}% OFF</span>
        </p>
      )}
      <div className="checkout-grid">
        <label>Subcategory<input data-testid="product-subcategory-input" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} placeholder="e.g. Pure Cotton" /></label>
      </div>
      <div className="option-group">
        <span className="option-label">Sizes — tap to select ({form.sizes.length} selected)</span>
        <div className="size-group">
          <small>Alpha sizes</small>
          <div className="option-row" data-testid="alpha-sizes-row">
            {ALPHA_SIZES.map((s) => (
              <button type="button" key={s} data-testid={`size-chip-${s.replace(/\s/g, "-")}`} className={form.sizes.includes(s) ? "option-chip active" : "option-chip"} onClick={() => setForm({ ...form, sizes: form.sizes.includes(s) ? form.sizes.filter((x) => x !== s) : [...form.sizes, s] })}>{s}</button>
            ))}
          </div>
        </div>
        <div className="size-group">
          <small>Tape measurement (inches)</small>
          <div className="option-row" data-testid="tape-sizes-row">
            {TAPE_SIZES.map((s) => (
              <button type="button" key={s} data-testid={`size-chip-${s}`} className={form.sizes.includes(s) ? "option-chip active" : "option-chip"} onClick={() => setForm({ ...form, sizes: form.sizes.includes(s) ? form.sizes.filter((x) => x !== s) : [...form.sizes, s] })}>{s}"</button>
            ))}
          </div>
        </div>
      </div>
      <div className="option-group">
        <span className="option-label">Colors — tap to select ({form.colors.length} selected)</span>
        <div className="option-row" data-testid="colors-row">
          {COLOR_OPTIONS.map(([name, hex]) => (
            <button
              type="button"
              key={name}
              data-testid={`color-chip-${name.toLowerCase()}`}
              className={form.colors.includes(name) ? "color-chip active" : "color-chip"}
              onClick={() => setForm({ ...form, colors: form.colors.includes(name) ? form.colors.filter((x) => x !== name) : [...form.colors, name] })}
            >
              <i className="color-dot" style={hex === "multi" ? { background: "linear-gradient(135deg,#C0392B,#C9A227,#1E8449,#2E5EAA,#7D3C98)" } : { background: hex }} />
              {name}
            </button>
          ))}
        </div>
      </div>
      <label>Description<textarea data-testid="product-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Fabric, work, occasion…" /></label>
      <div className="admin-images">
        {form.images.map((im) => (
          <div key={im} className="admin-image">
            <img src={imgSrc(im)} alt="Product" />
            <button type="button" data-testid={`remove-image-${im.slice(-8)}`} onClick={() => setForm({ ...form, images: form.images.filter((x) => x !== im) })} aria-label="Remove image"><X size={14} /></button>
          </div>
        ))}
        <label className={uploading ? "upload-tile disabled" : "upload-tile"} data-testid="product-image-upload">
          <Upload size={18} /> {uploading ? "Uploading…" : "Add photo"}
          <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={upload} disabled={uploading} />
        </label>
      </div>
      <div className="option-row">
        {[["in_stock", "In stock"], ["featured", "Featured"], ["new_arrival", "New arrival"]].map(([k, l]) => (
          <button type="button" key={k} data-testid={`flag-${k}`} className={form[k] ? "option-chip active" : "option-chip"} onClick={() => setForm({ ...form, [k]: !form[k] })}>{l}</button>
        ))}
      </div>
      <div className="cart-actions">
        <button type="button" className="text-link as-button" onClick={onCancel}>Cancel</button>
        <button data-testid="product-save-button" className="button primary" disabled={uploading}>{initial.id ? "Save changes" : "Add product"}</button>
      </div>
    </form>
  );
}

function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null); // null | EMPTY | product

  const load = () => {
    axios.get(`${API}/products`).then(({ data }) => setProducts(data)).catch(() => {});
    axios.get(`${API}/admin/orders`, { headers: authHeader() }).then(({ data }) => setOrders(data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async (form) => {
    const payload = {
      ...form,
      price: form.price === "" ? null : Number(form.price),
      mrp: form.mrp === "" ? null : Number(form.mrp),
      sizes: form.sizes,
    };
    try {
      if (form.id) await axios.put(`${API}/admin/products/${form.id}`, payload, { headers: authHeader() });
      else await axios.post(`${API}/admin/products`, payload, { headers: authHeader() });
      toast.success(form.id ? "Product updated" : "Product added");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save product");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await axios.delete(`${API}/admin/products/${id}`, { headers: authHeader() });
    toast.success("Product deleted");
    load();
  };

  return (
    <main className="inner-page">
      <div className="admin-head">
        <Chapter index="—" eyebrow="OWNER DASHBOARD" title={<>Manage your <em>store</em></>} />
        <button data-testid="admin-logout-button" className="button outline small-btn" onClick={onLogout}><LogOut size={14} /> Logout</button>
      </div>
      <div className="option-row admin-tabs">
        <button data-testid="tab-products" className={tab === "products" ? "option-chip active" : "option-chip"} onClick={() => setTab("products")}>Products ({products.length})</button>
        <button data-testid="tab-orders" className={tab === "orders" ? "option-chip active" : "option-chip"} onClick={() => setTab("orders")}>Orders ({orders.length})</button>
      </div>

      {tab === "products" && (
        <>
          {editing ? (
            <ProductForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />
          ) : (
            <>
              <button data-testid="add-product-button" className="button primary" onClick={() => setEditing({ ...EMPTY })}><Plus size={16} /> Add product</button>
              <div className="admin-product-list">
                {products.map((p) => (
                  <div className="admin-product-row" data-testid={`admin-product-${p.id}`} key={p.id}>
                    {p.images?.[0] && <img src={imgSrc(p.images[0])} alt={p.name} />}
                    <div>
                      <b>{p.name}</b>
                      <span>{CATEGORIES.find((c) => c.slug === p.category)?.name || p.category} · {p.price != null ? `₹${p.price.toLocaleString("en-IN")}` : "Price on enquiry"}{p.in_stock ? "" : " · Sold out"}</span>
                    </div>
                    <button data-testid={`edit-product-${p.id}`} className="icon-button" onClick={() => setEditing({ ...p, price: p.price ?? "", mrp: p.mrp ?? "" })} aria-label="Edit"><Pencil size={15} /></button>
                    <button data-testid={`delete-product-${p.id}`} className="icon-button" onClick={() => remove(p.id)} aria-label="Delete"><Trash2 size={15} /></button>
                  </div>
                ))}
                {!products.length && <p className="catalogue-note">No products yet — add your first one above.</p>}
              </div>
            </>
          )}
        </>
      )}

      {tab === "orders" && (
        <div className="admin-product-list">
          {orders.map((o) => (
            <div className="admin-order" data-testid={`admin-order-${o.id}`} key={o.id}>
              <div className="admin-order-head">
                <b>{o.name}</b> · <a href={`tel:${o.mobile}`}>{o.mobile}</a>
                <span className={`order-status ${o.payment_status}`}>{o.payment_status === "paid" ? "PAID" : o.payment_method.toUpperCase()}</span>
                <span className="order-status current" data-testid={`order-status-${o.id}`}>{(o.status || "placed").toUpperCase()}</span>
              </div>
              <p>{o.items.map((i) => `${i.name} ×${i.qty}${[i.size, i.color].filter(Boolean).length ? ` (${[i.size, i.color].filter(Boolean).join(" / ")})` : ""}`).join(", ")}</p>
              <p className="order-meta">
                ₹{o.total.toLocaleString("en-IN")}{o.delivery_charge ? ` (incl. ₹${o.delivery_charge} delivery)` : ""} · {o.delivery_type}{o.address ? ` · ${o.address}, ${o.city} ${o.pincode}` : ""} · {new Date(o.created_at).toLocaleString("en-IN")}
              </p>
              <div className="option-row status-row">
                {["confirmed", "packed", "shipped", "delivered", "cancelled"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`status-${s}-${o.id}`}
                    className={(o.status || "placed") === s ? "option-chip active" : "option-chip"}
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/admin/orders/${o.id}/status`, { status: s }, { headers: authHeader() });
                        toast.success(`Order marked ${s} — customer SMS sent`);
                        load();
                      } catch {
                        toast.error("Could not update status");
                      }
                    }}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!orders.length && <p className="catalogue-note">No orders yet.</p>}
        </div>
      )}
    </main>
  );
}

export default function Admin() {
  const [loggedIn, setLoggedIn] = useState(() => !!localStorage.getItem("hsm_admin"));
  const logout = () => {
    localStorage.removeItem("hsm_admin");
    setLoggedIn(false);
  };
  return (
    <>
      <Header />
      {loggedIn ? <AdminDashboard onLogout={logout} /> : <AdminLogin onLogin={() => setLoggedIn(true)} />}
      <Footer />
    </>
  );
}
